import React, { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../components/ui/dialog";
import { toDateInput, fromDateInput } from "./utils";
import { Save } from "lucide-react";
import TierPreview from "./TierPreview";

export default function EditFirmDialog({ open, onOpenChange, workspace, onDone }) {
  const [f, setF] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tiers, setTiers] = useState([]);
  const [tiersLoading, setTiersLoading] = useState(false);

  useEffect(() => {
    if (!workspace) return;
    setF({
      name: workspace.name || "",
      plan_tier: workspace.plan_tier || "",
      subscription_start_date: toDateInput(workspace.subscription_start_date),
      subscription_end_date: toDateInput(workspace.subscription_end_date),
    });
  }, [workspace]);

  const loadTiers = useCallback(async () => {
    setTiersLoading(true);
    try {
      const { data } = await api.get("/superadmin/subscription-tiers");
      // Keep archived tiers OUT of the dropdown so admins can't downgrade a
      // firm onto a retired plan.
      setTiers((data.tiers || []).filter((t) => !t.archived));
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setTiersLoading(false);
    }
  }, []);

  // Refetch each time the dialog opens so freshly created / edited tiers
  // show up immediately.
  useEffect(() => {
    if (open) loadTiers();
  }, [open, loadTiers]);

  const upd = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.plan_tier) { toast.error("Pick a subscription tier"); return; }
    setLoading(true);
    try {
      await api.patch(`/superadmin/workspaces/${workspace.workspace_id}`, {
        name: f.name,
        plan_tier: f.plan_tier,
        subscription_start_date: fromDateInput(f.subscription_start_date),
        subscription_end_date: fromDateInput(f.subscription_end_date),
      });
      toast.success("Saved");
      onDone();
      onOpenChange(false);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };

  if (!f) return null;

  const selectedTier = tiers.find((t) => t.slug === f.plan_tier) || null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md rounded-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Edit ID firm</DialogTitle>
          <DialogDescription>Update this firm's details and subscription.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="text-xs uppercase tracking-widest font-bold text-[#7A7571] mb-2 block">Company name</label>
            <input data-testid="edit-firm-name" required className="input-soft" value={f.name} onChange={(e) => upd("name", e.target.value)} />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest font-bold text-[#7A7571] mb-2 block">Package</label>
            <select
              data-testid="edit-firm-tier-select"
              required
              value={f.plan_tier}
              onChange={(e) => upd("plan_tier", e.target.value)}
              className="input-soft"
              disabled={tiersLoading}
            >
              {tiersLoading && <option value="">Loading tiers…</option>}
              {/* Legacy firms may have a plan_tier that references an
                  archived / deleted tier. Show it as a placeholder option
                  so the select doesn't silently reset to the first tier. */}
              {!tiersLoading && f.plan_tier && !tiers.find((t) => t.slug === f.plan_tier) && (
                <option value={f.plan_tier}>{f.plan_tier} (not in active catalog)</option>
              )}
              {!tiersLoading && tiers.length === 0 && !f.plan_tier && (
                <option value="">No active tiers — create one first</option>
              )}
              {!tiersLoading && tiers.map((t) => (
                <option key={t.tier_id} value={t.slug} data-testid={`edit-firm-tier-option-${t.slug}`}>
                  {t.name}
                  {typeof t.price_monthly_cents === "number" ? ` — ${t.currency} ${(t.price_monthly_cents / 100).toLocaleString(undefined, { maximumFractionDigits: 0 })}/mo` : " — Contact sales"}
                </option>
              ))}
            </select>
            {selectedTier && <TierPreview tier={selectedTier} />}
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs uppercase tracking-widest font-bold text-[#7A7571] mb-2 block">Start</label>
              <input data-testid="edit-firm-start" type="date" className="input-soft" value={f.subscription_start_date} onChange={(e) => upd("subscription_start_date", e.target.value)} />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest font-bold text-[#7A7571] mb-2 block">End</label>
              <input data-testid="edit-firm-end" type="date" className="input-soft" value={f.subscription_end_date} onChange={(e) => upd("subscription_end_date", e.target.value)} />
            </div>
          </div>
          <button data-testid="edit-firm-submit" disabled={loading || !f.plan_tier} className="btn-primary w-full inline-flex items-center justify-center gap-2 disabled:opacity-60">
            <Save size={16} /> {loading ? "Saving…" : "Save changes"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
